import React, { useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl-next';

import Select, { CreatableSelect } from '@atlaskit/select';
import { PRIORITIES } from '@atlassian/eoc-priority-badge';

import { type DropdownPriority, type PriorityType } from '../../common/types';

import { PriorityDropdownMessages } from './messages';
import { PriorityOption } from './priority-option';
import { PriorityValue } from './priority-value';
import { type PriorityDropdownProps } from './types';

export const PriorityDropdown = <IsMulti extends boolean = false>({
	showDynamicPriority,
	isReadOnly,
	isCreatable,
	onChange,
	id,
	...props
}: PriorityDropdownProps<IsMulti>) => {
	const { formatMessage } = useIntl();
	const initialValue = useRef(props.value); // We need to hold a ref to the initial value to not to lose it when the user changed the value
	const [lastCreatedOption, setLastCreatedOption] = useState<PriorityType | null>(null);

	const priorityOptions = useMemo(() => {
		const tempValues: PriorityType[] = Object.entries(PRIORITIES).map(([value, { label }]) => ({
			label: label,
			value: value as DropdownPriority,
		}));

		return tempValues;
	}, []);

	const options = useMemo(() => {
		const newOptions = [...priorityOptions];

		if (showDynamicPriority) {
			newOptions.unshift({
				label: '{{priority}}',
				value: '{{priority}}',
			});
		}

		// If it is creatable select and the initial value is present, we need to add this value into the options (if it is not already there)
		if (isCreatable && !!initialValue.current) {
			if (props.isMulti) {
				const initialOptions = initialValue.current as PriorityType[];
				initialOptions.forEach((initialOption) => {
					if (!newOptions.some(({ value }) => value === initialOption.value)) {
						newOptions.unshift(initialOption);
					}
				});
			} else {
				const initialOption = initialValue.current as PriorityType;
				if (!newOptions.some(({ value }) => value === initialOption.value)) {
					newOptions.unshift(initialOption);
				}
			}
		}

		if (isCreatable && lastCreatedOption) {
			newOptions.unshift(lastCreatedOption);
		}

		return newOptions;
	}, [isCreatable, lastCreatedOption, priorityOptions, props.isMulti, showDynamicPriority]);

	const SelectComponent = isCreatable ? CreatableSelect : Select;

	return (
		<SelectComponent<PriorityType, IsMulti>
			isSearchable={!isReadOnly}
			menuIsOpen={isReadOnly ? false : undefined}
			defaultValue={priorityOptions[2]}
			components={{
				SingleValue: (props) => <PriorityValue {...props} isCreatable={isCreatable} />,
				Option: PriorityOption,
				...props.components,
			}}
			options={options}
			formatCreateLabel={(inputValue) =>
				`"${inputValue}" (${formatMessage(PriorityDropdownMessages.CustomPriority)})`
			}
			onChange={(value, actionMeta) => {
				if (actionMeta.action === 'create-option') {
					setLastCreatedOption(actionMeta.option);
				}
				onChange?.(value, actionMeta);
			}}
			filterOption={(option, inputValue) => {
				return option.label.toLowerCase().includes(inputValue.toLowerCase());
			}}
			inputId={id}
			{...props}
		/>
	);
};
